import { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import pageOverrides from '../pages.json';

export const SISTER_ALIAS = 'sister-properties';
export const SISTER_PATH = '/sisterproperties';

/**
 * Peek-only definition for reading SisterProperties community slot groups.
 * Not declared on consumer contentSchemas — SisterProperties owns those
 * editable wrappers. Field shapes must stay aligned with SisterProperties.tsx.
 */
const sisterCommunityPeekDef: SlotGroupDefinition = {
  label: 'Sister Community',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isSisterCommunityFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

/** Page is eligible when visible and still hosts the SisterProperties section. */
export function isSisterPropertiesPageActive(): boolean {
  const page = (
    pageOverrides as Record<
      string,
      { visible?: boolean; path?: string; sections?: { slot?: string }[] }
    >
  )[SISTER_ALIAS];
  // No local page entry → inherit parent theme page (active by default).
  if (!page) return true;
  if (page.visible === false) return false;
  // Path-only (or other shallow) overrides omit sections — treat as inherited.
  if (page.sections === undefined) return true;
  return page.sections.some((section) => section.slot === 'SisterProperties');
}

/**
 * True when the Sister Properties page is active and at least one community
 * card has content. Used to gate nav / sitemap / thank-you CTA defaults.
 */
export function useHasSisterProperties(): boolean {
  const sister1 = useSlotGroup('sisterproperties.community1', sisterCommunityPeekDef);
  const sister2 = useSlotGroup('sisterproperties.community2', sisterCommunityPeekDef);
  const sister3 = useSlotGroup('sisterproperties.community3', sisterCommunityPeekDef);
  const sister4 = useSlotGroup('sisterproperties.community4', sisterCommunityPeekDef);
  const sister5 = useSlotGroup('sisterproperties.community5', sisterCommunityPeekDef);
  const sister6 = useSlotGroup('sisterproperties.community6', sisterCommunityPeekDef);

  return useMemo(
    () =>
      isSisterPropertiesPageActive() &&
      [sister1, sister2, sister3, sister4, sister5, sister6].some(isSisterCommunityFilled),
    [sister1, sister2, sister3, sister4, sister5, sister6],
  );
}
